const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
class AuthService {
  /**
   * Register a new user
   */
  async register(userData) {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.success && data.accessToken) {
        // Store tokens
        this.setTokens(data.accessToken, data.refreshToken);
        // Store user data
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Login user
   */
  async login(email, password) {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.accessToken) {
        // Store tokens
        this.setTokens(data.accessToken, data.refreshToken);
        // Store user data
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (refreshToken) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });
      }

      // Clear local storage
      this.clearTokens();
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear tokens anyway
      this.clearTokens();
      localStorage.removeItem('user');
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (data.success && data.accessToken) {
        this.setAccessToken(data.accessToken);
      } else {
        // Refresh failed, logout user
        this.logout();
      }

      return data;
    } catch (error) {
      console.error('Refresh token error:', error);
      this.logout();
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  async getProfile() {
    try {
      const response = await this.authenticatedFetch(`${API_URL}/auth/profile`);
      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    try {
      const response = await this.authenticatedFetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      if (response.success && response.user) {
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await this.authenticatedFetch(`${API_URL}/auth/change-password`, {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      return response;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Make authenticated API call with automatic token refresh
   */
  async authenticatedFetch(url, options = {}) {
    const token = this.getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    // Check if this is a FormData request
    const isFormData = options.isFormData || options.body instanceof FormData;
    
    // Build headers - don't set Content-Type for FormData
    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };
    
    // Only add Content-Type if not FormData
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If token expired, try to refresh and retry
    if (response.status === 403) {
      await this.refreshToken();
      const newToken = this.getAccessToken();

      if (newToken) {
        const retryHeaders = {
          'Authorization': `Bearer ${newToken}`,
          ...options.headers,
        };
        
        if (!isFormData) {
          retryHeaders['Content-Type'] = 'application/json';
        }

        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });

        return retryResponse.json();
      }
    }

    return response.json();
  }

  /**
   * Convenience method for GET requests
   */
  async get(url) {
    return this.authenticatedFetch(url);
  }

  /**
   * Convenience method for POST requests
   */
  async post(url, data, options = {}) {
    const isFormData = data instanceof FormData;
    return this.authenticatedFetch(url, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      isFormData,
      ...options
    });
  }

  /**
   * Convenience method for PUT requests
   */
  async put(url, data, options = {}) {
    const isFormData = data instanceof FormData;
    return this.authenticatedFetch(url, {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      isFormData,
      ...options
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  async delete(url) {
    return this.authenticatedFetch(url, {
      method: 'DELETE'
    });
  }

  /**
   * Token management methods
   */
  setTokens(accessToken, refreshToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  setAccessToken(accessToken) {
    localStorage.setItem('accessToken', accessToken);
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  /**
   * Check if user is club owner (highest privilege)
   */
  isAdmin() {
    return this.hasRole('club_owner');
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.roles && user.roles.includes(role);
  }

  /**
   * Check if user is a super admin
   */
  isSuperAdmin() {
    return this.hasRole('super_admin');
  }

  /**
   * Check if user is a club owner
   */
  isClubOwner() {
    return this.hasRole('club_owner');
  }

  /**
   * Check if user is regular user
   */
  isUser() {
    return this.hasRole('user');
  }

  /**
   * Check if user's email is verified
   */
  isVerified() {
    const user = this.getCurrentUser();
    return user && user.is_verified === true;
  }
}

export default new AuthService();
